import * as React from "react";
import {useEffect, useState} from "react";
import {useQuery } from "@tanstack/react-query";
import MigrationStatusChip from "../common/MigrationStatusChip";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import {EmptyFolder} from "../../models/EmptyFolder";
import {useTableHandlers} from "../common/Table/Hooks/UseTableHandlers";
import {useTableFileFilters} from "../common/Table/Hooks/UseTableFileFilters";
import {QUERY_KEYS} from "../../constants/QueryKeys";
import {QUERY_CONFIG} from "../../constants/QueryConfig";
import {SearchInput} from "../common/Table/SearchInput";
import {FileStatusFilter} from "../common/Table/File/FileStatusFilter";
import TablePagination from "../common/Table/TablePagination";
import FirmDocumentsFoldersRepository from "../../repositories/FirmDocumentsFoldersRepository";
import {cn} from "@/lib/utils";
import {Table, TableHeader, TableBody, TableRow, TableHead, TableCell} from "@/components/ui/table";
import {tableWrapperClassName} from "../common/Table/TableStyles";

interface FirmDocumentsFolderEmptyFoldersTableProps {
  firmDocumentsFolderId: string;
  toolbar?: React.ReactNode;
}

export const FirmDocumentsFolderEmptyFoldersTable: React.FC<FirmDocumentsFolderEmptyFoldersTableProps> = ({
  firmDocumentsFolderId,
  toolbar,
}: FirmDocumentsFolderEmptyFoldersTableProps) => {
  const firmDocumentsFoldersRepository = new FirmDocumentsFoldersRepository();
  const [firmDocumentsFolderEmptyFolders, setFirmDocumentsFolderEmptyFolders] = useState<EmptyFolder[]>([]);

  const { order, sort, page, rowsPerPage, rows, setRows, setPage, changeSort, changeOrder, handleRowsPerPageChange } = useTableHandlers({ order: "asc", sort: "status", page: 0, rowsPerPage: 100 });
  const { statusFilter, destinationPathFilter, setStatusFilter, handleDestinationPathSearch } = useTableFileFilters();

  const firmDocumentsFolderEmptyFoldersQuery = useQuery({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS_FOLDER_EMPTY_FOLDERS, firmDocumentsFolderId, page, rowsPerPage, statusFilter, sort, order, destinationPathFilter], queryFn:
    async () => await firmDocumentsFoldersRepository.getFirmDocumentsFolderEmptyFoldersForFirmDocumentsFolder(firmDocumentsFolderId, {
      status: statusFilter ? [statusFilter] : null,
      uploadPathFromRoot: destinationPathFilter,
      descending: order === "desc",
      orderBy: sort,
      pageNumber: page + 1,
      pageSize: rowsPerPage,
    }), staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME, refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL, refetchIntervalInBackground: true, refetchOnMount: true, });

  useEffect(() => {
    if (firmDocumentsFolderEmptyFoldersQuery.data?.data) {
      setFirmDocumentsFolderEmptyFolders(firmDocumentsFolderEmptyFoldersQuery.data?.data);
      setRows(firmDocumentsFolderEmptyFoldersQuery.data.paging?.totalCount ?? 0);
    }
  }, [firmDocumentsFolderEmptyFoldersQuery]);

  const loading = firmDocumentsFolderEmptyFoldersQuery.isFetching || firmDocumentsFolderEmptyFoldersQuery.isError;

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      {toolbar}
      <div className="hidden sm:flex flex-wrap gap-3 rounded-md pb-1">
        <SearchInput label={"Search empty folders"} placeholder={"Folder path"} onBlur={(e) => handleDestinationPathSearch(e.target.value)} />
        <FileStatusFilter value={statusFilter} onChange={(newValue) => { setPage(0); setStatusFilter(newValue); }} />
      </div>
      <div className={cn(tableWrapperClassName, loading && "opacity-50 pointer-events-none")}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-2.5" />
              <TableHead className="w-[35%]">Folder ID</TableHead>
              <TableHead className="w-[15%]"><SortableTableColumnHeader label={"Status"} sortProperty={"status"} getSort={() => sort} setSort={(newSort) => changeSort(newSort)} getOrder={() => order} setOrder={(newOrder) => changeOrder(newOrder)} /></TableHead>
              <TableHead className="w-[50%]">Sharepoint folder path</TableHead>
              <TableHead className="w-2.5" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {firmDocumentsFolderEmptyFolders.map((firmDocumentsFolderEmptyFolder) => (
              <TableRow key={firmDocumentsFolderEmptyFolder.id}>
                <TableCell />
                <TableCell><span className="text-sm">{firmDocumentsFolderEmptyFolder.id}</span></TableCell>
                <TableCell><MigrationStatusChip status={firmDocumentsFolderEmptyFolder.status} /></TableCell>
                <TableCell className="whitespace-normal break-all">{firmDocumentsFolderEmptyFolder.uploadPathFromRoot}</TableCell>
                <TableCell />
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination page={page} rowsPerPage={rowsPerPage} rows={rows} onPageChange={setPage} onRowsPerPageChange={handleRowsPerPageChange} />
      </div>
    </div>
  );
};
